import * as styleConstants from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { ContentWrapper } from "@affinity/Overrides/Widgets/Basic/RichContent";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import styled from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer1?: GridContainerProps;
    widgetGridContainer2?: GridContainerProps;
    logoGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    copyrightGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

/* 
Left align the logo to container including the mobile view
Same for WYSIWYG area on min size, then 3 padding levels for 3 desktop scales; 80, 70, 100 ??? with logo width being 60, 60, 70
*/

const LogoWrapper = styled(GridItem)`
    width: 100%;
    padding: 0;
    display: flex;
    justify-content: left;
    img {
        height: auto;
        max-width: 120px;
        @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
            max-width: 140px;
        }
    }
`;

const FooterTopContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: ${styleHelpers.rem(30)} ${styleHelpers.rem(20)};
    gap: ${styleHelpers.rem(20)};
    max-width: ${styleHelpers.breakpointWidth("xl")};
    @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
        flex-direction: row;
        padding: ${styleHelpers.rem(40)};
    }
    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        padding: ${styleHelpers.rem(50)} ${styleHelpers.rem(60)};
    }
    ${ContentWrapper} {
        color: ${styleConstants.colours.white};
    }
`;

const FooterBottomContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: ${styleHelpers.rem(20)};
    gap: ${styleHelpers.rem(20)};
    max-width: ${styleHelpers.breakpointWidth("xl")};
    @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
        flex-direction: row;
        justify-content: space-between;
        padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(40)};
    }
    @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(60)};
    }
`;

const FooterBottomItem = styled.div`
    div {
        a,
        p {
            color: ${styleConstants.colours.white};
            text-transform: none;
            font-size: ${styleHelpers.rem(16)} !important;
        }
        a:hover {
            color: ${styleConstants.colours.white};
            text-decoration: none;
        }
        display: flex;
        flex-direction: column;
        .list-wrapper {
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: ${styleHelpers.rem(20)};
            @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
                flex-direction: row;
                gap: 0;
            }
        }
        .links-wrappe {
            margin: 0;
        }
        .title-wrapper:has(h3:empty) {
            display: none;
        }
    }
`;

const FooterWrapper = styled.div<InjectableCss>`
    max-width: 100% !important;
    background: ${styleConstants.colours.darkBlue};
`;

const Line = styled.hr`
    border: 0;
    width: 100%;
    border-top: 1px solid ${styleConstants.colours.white};
`;

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    return (
        <FooterWrapper>
            <FooterTopContainer>
                <LogoWrapper width={[12, 12, 3, 3, 3]}>
                    <Zone zoneName="Logo" contentId={id} />
                </LogoWrapper>
                <GridItem width={[12, 12, 8, 8, 8]}>
                    <Zone zoneName="RichContent" contentId={id} />
                </GridItem>
            </FooterTopContainer>
            <Line />
            <FooterBottomContainer>
                <FooterBottomItem>
                    <Zone zoneName="SiteMapLinks" contentId={id} />
                </FooterBottomItem>
                <FooterBottomItem>
                    <Zone zoneName="Copyright" contentId={id} />
                </FooterBottomItem>
            </FooterBottomContainer>
        </FooterWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
